import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Voucher } from '../types/voucher';
import { VoucherService } from '../services/voucherService';

interface VoucherState {
  vouchers: Voucher[];
  selectedVoucher: Voucher | null;
  loading: boolean;
  error: string | null;
  dialogOpen: boolean;

  fetchVouchers: (idPeriodo: number, codigoFuente?: string) => Promise<void>;
  saveVoucher: (voucher: Partial<Voucher>) => Promise<void>;
  anularVoucher: (id: number) => Promise<void>;
  deleteVoucher: (id: number) => Promise<void>;
  setSelectedVoucher: (voucher: Voucher | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
}

export const useVoucherStore = create<VoucherState>()(
  devtools(
    (set, get) => ({
      vouchers: [],
      selectedVoucher: null,
      loading: false,
      error: null,
      dialogOpen: false,

      fetchVouchers: async (idPeriodo, codigoFuente?) => {
        set({ loading: true, error: null }, false, 'voucher/fetch/start');
        try {
          const vouchers = await VoucherService.getByPeriodo(idPeriodo, codigoFuente);
          set({ vouchers, loading: false }, false, 'voucher/fetch/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cargar vouchers';
          set({ loading: false, error: errorMessage }, false, 'voucher/fetch/error');
        }
      },

      saveVoucher: async (voucher) => {
        set({ loading: true, error: null }, false, 'voucher/save/start');
        try {
          if (voucher.idVoucher && voucher.idVoucher > 0) {
            await VoucherService.update(voucher.idVoucher, voucher);
          } else {
            await VoucherService.create(voucher);
          }
          set({ loading: false, dialogOpen: false, selectedVoucher: null }, false, 'voucher/save/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al guardar voucher';
          set({ loading: false, error: errorMessage }, false, 'voucher/save/error');
          throw error;
        }
      },

      anularVoucher: async (id) => {
        set({ loading: true, error: null }, false, 'voucher/anular/start');
        try {
          await VoucherService.anular(id);
          set((state) => ({
            vouchers: state.vouchers.map(v =>
              v.idVoucher === id ? { ...v, estado: 'ANULADO' } : v
            ),
            loading: false,
          }), false, 'voucher/anular/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al anular voucher';
          set({ loading: false, error: errorMessage }, false, 'voucher/anular/error');
          throw error;
        }
      },

      deleteVoucher: async (id) => {
        set({ loading: true, error: null }, false, 'voucher/delete/start');
        try {
          await VoucherService.delete(id);
          set((state) => ({
            vouchers: state.vouchers.filter(v => v.idVoucher !== id),
            loading: false,
          }), false, 'voucher/delete/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al eliminar voucher';
          set({ loading: false, error: errorMessage }, false, 'voucher/delete/error');
          throw error;
        }
      },

      setSelectedVoucher: (voucher) => set({ selectedVoucher: voucher }, false, 'voucher/setSelected'),
      openDialog: () => set({ dialogOpen: true }, false, 'voucher/openDialog'),
      closeDialog: () => set({ dialogOpen: false, selectedVoucher: null }, false, 'voucher/closeDialog'),
      clearError: () => set({ error: null }, false, 'voucher/clearError'),
    }),
    { name: 'VoucherStore' }
  )
);
